(function (angular) {
    'use strict';
    var app = angular.module('enervee');

    /**
     * Manages items and callbacks for the generic cartDirective, providing
     * methods to set callbacks and connect the generic cart with other cart
     * and checkout services, such as Enervee Checkout.
     */
    app.factory('CartService', [
        '$q',
        '$timeout',
        '$window',
        '$location',
        '$log',
        'UtilService',
        function (
            $q,
            $timeout,
            $window,
            $location,
            $log,
            UtilService
        ) {
            var _pub = {
                cartDetails: {
                    itemCount: 0,
                    cartId: null,
                    fulfillmentPartnerName: '',
                    fulfillmentPartnerId: null,
                    redirectUrl: null
                },
            };
            var _cartOpenCallback;
            var _cartCloseCallback;
            var _cartQuantityUpdateCallbacks = [];
            var _cartCheckoutCallback;
            var _cartInitialized = $q.defer();
            var _cartItems = [];

            /**
             * Get all of the cart items.
             *
             * @return {Object[]}
             */
            _pub.getItems = function () {
                return _cartItems;
            };

            /**
             * Gets an item from the cart by its `id` property.
             *
             * @param {Number|String} id
             * @return {Object|undefined}
             */
            _pub.getItemById = function (id) {
                for (var i = 0; i < _cartItems.length; i++) {
                    if (_cartItems[i].id === id) {
                        return _cartItems[i];
                    }
                }
            };

            /**
             * Helper function to clear all items from the cart.
             */
            _pub.clearAllItems = function () {
                _cartItems.length = 0;
                _pub.triggerCartQuantityUpdateCallbacks();
            };

            /**
             * Adds an item to the cart, extracting the appropriate properties
             * and pushing copies into the cart items array.
             * Triggers the quantity update callbacks when finished.
             * NOTE: Quantity passed is _added_ to existing quantity.
             * NOTE: this method is best called _specifically_ by the service
             * that manages the backend cart, _after_ the backend cart has been
             * successfully updated.
             *
             * @param {Object} item a product to be added to the cart
             * @param {String} [item.title]
             * @param {String} item.brand only used if item.title doesn't exist
             * @param {String} item.model_number only used if item.title
             * doesn't exist
             * @param {Number|String} item.id
             * @param {Number|String} item.category_id
             * @param {Number} item.quantity
             * @param {Number|String} item.price
             * @param {String} item.image the src for the item's image
             * @param {Object} item.rebate rebate data for this product
             * @param {Number} item.rebate.id
             * @param {Number} item.rebate.amount
             * @param {Number} item.rebate.quantity
             * @param {Object} [options] a hash of options
             * @param {Object} [options.quantityMode='increment'] the mode to use when updating
             * product quantities
             */
            _pub.addItem = function (item, options) {
                options = options || {};
                var itemIds = _cartItems.map(function (item) {
                    return item.id;
                });
                var itemTitle = item.title;
                if (!itemTitle) {
                    itemTitle = item.brand + " " + item.model_number;
                }
                if (itemIds.indexOf(item.id) > -1) {
                    setItemQuantity(item, options.quantityMode);
                    setItemRebates(item.id, item.rebates);
                    setItemAdditionalDiscount(item.id, item.total_additional_discounts);
                } else {
                    _cartItems.push({
                        id: item.id,
                        category_id: item.category_id,
                        title: itemTitle,
                        quantity: item.quantity || 1,
                        price: item.price,
                        total_additional_discounts: parseFloat(item.total_additional_discounts || "0.00"),
                        image: item.image,
                        rebates: item.rebates,
                    });
                }
                _pub.triggerCartQuantityUpdateCallbacks();
            };

            /**
             * Resolves a promise to indicate that the cart has finished
             * loading its initial UI.
             */
            _pub.cartFinishInit = function () {
                _cartInitialized.resolve();
            };

            /**
             * Sets the callback for when `cartOpen` is called.
             *
             * @param {Function} cartOpenCallback
             */
            _pub.setCartOpenCallback = function (cartOpenCallback) {
                _cartOpenCallback = cartOpenCallback;
            };

            /**
             * Sets the callback for when `cartClose` is called.
             *
             * @param {Function} cartCloseCallback
             */
            _pub.setCartCloseCallback = function (cartCloseCallback) {
                _cartCloseCallback = cartCloseCallback;
            };

            /**
             * Adds a callback for when the cart's quantity is updated.
             *
             * @param {Function} cartCloseCallback
             */
            _pub.addCartQuantityUpdateCallback = function (cartQuantityUpdateCallback) {
                _cartQuantityUpdateCallbacks.push(cartQuantityUpdateCallback);
            };

            _pub.triggerCartQuantityUpdateCallbacks = function () {
                _cartInitialized.promise.then(function () {
                    _cartQuantityUpdateCallbacks.forEach(function (callback) {
                        callback.apply({});
                    });
                });
            };

            _pub.addCartQuantityUpdateCallback(updateCartItemCount);

            /**
             * Sets the callback for when `checkout` is called.
             *
             * @param {Function} checkoutCallback
             */
            _pub.setCheckoutCallback = function (checkoutCallback) {
                _cartCheckoutCallback = checkoutCallback;
            };

            /**
             * Utility function to trigger callbacks and open the cart.
             * @param resetView - should we reset the view to total?
             */
            _pub.openCart = function (resetView) {
                /**
                 * $timeout ensures that the current digest cycle completes
                 * before calling the below method, which may need to use
                 * $scope.$applyAsync() or update its DOM with bound values.
                 */
                resetView = resetView || false;
                $timeout(function () {
                    _cartOpenCallback.apply({}, [resetView]);
                });
            };

            /**
             * Utility function to trigger callbacks and close the cart.
             */
            _pub.closeCart = function () {
                // See above comment regarding use of $timeout
                $timeout(function () {
                    _cartCloseCallback.apply({});
                });
            };

            /**
             * Utility function to trigger callbacks and go to checkout.
             */
            _pub.checkout = function () {
                // See above comment regarding use of $timeout
                $timeout(function () {
                    _cartCheckoutCallback.apply({});
                });
            };

            _pub.getProductIdsByCategoryId = function () {
                return UtilService.groupByProperty(_cartItems, 'category_id', function (product) {
                    return product.id;
                });
            };

            checkAutoOpenCartParam();

            /**
             * Given an item's id, increments the quantity of that item by the
             * given quantity or 1.
             *
             * @param {Object} item
             * @param {Number|String} itemId
             * @param {Number} quantity
             */
            function setItemQuantity(item, mode) {
                mode = mode || 'increment';
                var product = _pub.getItemById(item.id);
                if (!product) {
                    return;
                }
                switch (mode) {
                    case 'increment': {
                        product.quantity += item.quantity || 1;
                        break;
                    }
                    case 'set': {
                        if (typeof item.quantity === 'undefined') {
                            $log.warn('Warning: cannot set quantity of product to undefined', product);
                            break;
                        }
                        product.quantity = item.quantity;
                        break;
                    }
                }
            }

            /**
             * Given an item id, sets the rebate data for that item.
             *
             * @param {Number} id
             * @param {Object} rebate
             */
            function setItemRebates(id, rebates) {
                var product = _pub.getItemById(id);
                if (!product) {
                    return;
                }
                product.rebates = rebates;
            }

            /**
             * Given an item id, sets the new total_additional_discounts for that item.
             *
             * @param {Number} id
             * @param {Float} totalAdditionalDiscounts
             */
            function setItemAdditionalDiscount(id, totalAdditionalDiscounts) {
                var product = _pub.getItemById(id);
                if (!product) {
                    return;
                }
                product.total_additional_discounts = parseFloat(totalAdditionalDiscounts || "0.00");
            }

            /**
             * Helper function to update the cartItemCount, gets triggered whenever
             * a product's quantity changes.
             */
            function updateCartItemCount() {
                var totalItemCount = _cartItems.reduce(function (total, item) {
                    return total + item.quantity;
                }, 0);
                _pub.cartDetails.itemCount = totalItemCount;
            }

            /**
             * Checks the cart_open parameter, and if it exists in the url, it opens up the cart
             *
             */
            function checkAutoOpenCartParam() {
                const openCardParameter = 'cart_open';
                const shouldOpenCard = $location.search().hasOwnProperty(openCardParameter);
                if (shouldOpenCard) {
                    _pub.openCart(true);
                }
            }

            return _pub;
        }
    ]);
})(angular);
